import { Close } from '@mui/icons-material';
import { Box, IconButton, SelectChangeEvent, Stack } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  appointmentLengths,
  minimumTimeInAdvanceOptions,
  workingHoursEndTimeOptions,
  workingHoursStartTimeOptions,
} from '../../../constants/agentConstants';
import { AnalyticsEvents } from '../../../enums/AnalyticsEvents';
import { handleConnectToCalendar } from '../../../helpers/agentHelper';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentCapability } from '../../../interfaces/Agent';
import { WorkflowType } from '../../../interfaces/App';
import { allCalendarProviders } from '../../../interfaces/Calendar';
import { FeatureGate } from '../../../interfaces/FeatureGate';
import { checkCalendarConnection } from '../../../services/calendarService';
import StyledFormItem from '../../../styles/StyledFormItem';
import Button from '../../shared/Button/Button';
import Dropdown from '../../shared/DropDown/DropDown';
import Text from '../../shared/Text/Text';
import StyledCountryCodeLabel from '../AgentLineStep/StyledCountryCodeLabel';
import StyledFlag from '../AgentLineStep/StyledFlag';
import ConnectedCalendarIndicator from './ConnectedCalendar/ConnectedCalendarIndicator';

interface BookAppointmentsCapabilitySectionProps {
  agentId?: string;
  workflow?: WorkflowType;
}

const BookAppointmentsCapabilitySection: React.FC<
  BookAppointmentsCapabilitySectionProps
> = ({ agentId, workflow }) => {
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [timeError, setTimeError] = useState<string>('');
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const { control, watch, setValue, clearErrors } = useFormContext();
  const { fields } = useFieldArray({
    control,
    name: 'insert_appointment',
  });

  const { track } = useAnalytics();
  const { t } = useTranslation();
  const isBookingCapabilityV3Enabled = useIsFeatureEnabled(
    FeatureGate.BOOKING_CAPABILITY_V3
  );

  // Watch the insert_appointment field to get current values
  const bookAppointmentsValues = watch('insert_appointment');
  const selectedCapability = watch('external_capabilities') || '';

  // Check if we should display book appointments based on capability selection or existing data
  const shouldDisplayBookAppointments =
    selectedCapability === 'insert_appointment' ||
    (Array.isArray(selectedCapability) &&
      selectedCapability.includes('insert_appointment')) ||
    (bookAppointmentsValues && bookAppointmentsValues.length > 0);

  useEffect(() => {
    // Clear OAuth flag when component unmounts
    return () => {
      sessionStorage.removeItem('calendar_oauth_in_progress');
    };
  }, []);

  useEffect(() => {
    // Check if user is returning from OAuth and clear the flag
    if (sessionStorage.getItem('calendar_oauth_in_progress')) {
      sessionStorage.removeItem('calendar_oauth_in_progress');
    }
  }, []);

  // Clear form data if capability is not selected
  useEffect(() => {
    if (!shouldDisplayBookAppointments) {
      setValue('insert_appointment', []);
    }
  }, [shouldDisplayBookAppointments]);

  const handleRemoveCapability = async () => {
    // Clear the entire book appointments capability
    setValue('insert_appointment', []);
    setValue('external_capabilities', '');

    // Clear any validation errors for the insert_appointment fields
    clearErrors('insert_appointment');

    // Clear sessionStorage data for this capability
    try {
      const currentState = JSON.parse(
        sessionStorage.getItem('elora_agent_form_state') || '{}'
      );
      const updatedState = {
        ...currentState,
        insert_appointment: [],
        external_capabilities: '',
      };
      sessionStorage.setItem(
        'elora_agent_form_state',
        JSON.stringify(updatedState)
      );
    } catch (error) {
      console.warn('Failed to update sessionStorage:', error);
    }

    // Clear calendar auth state from localStorage if agent has an ID
    if (agentId) {
      try {
        const { clearCalendarAuthState } = await import(
          '../../../services/calendarService'
        );
        clearCalendarAuthState(agentId);
      } catch (error) {
        console.warn('Failed to clear calendar auth state:', error);
      }
    }

    // Track the removal
    track(AnalyticsEvents.AGENT_CAPABILITY_MODIFIED, {
      action: 'removed',
      capability: AgentCapability.INSERT_APPOINTMENT,
    });
  };

  const handleStartTimeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setStartTime(value);
    validateTimes(value, endTime);
  };

  const handleEndTimeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setEndTime(value);
    validateTimes(startTime, value);
  };

  const validateTimes = (start: string, end: string) => {
    if (start && end) {
      if (start >= end) {
        setTimeError('End time must be after start time');
      } else {
        setTimeError('');
      }
    } else {
      setTimeError('');
    }
  };

  const availableCalendarProviders = useMemo(() => {
    const providers = { ...allCalendarProviders };
    // Filter out Google Calendar if booking_capability_v3 is not enabled
    if (!isBookingCapabilityV3Enabled && providers.google) {
      delete providers.google;
    }
    return providers;
  }, [isBookingCapabilityV3Enabled]);

  const listOfSupportedCalendars = Object.keys(availableCalendarProviders).map(
    calendar => ({
      label: (
        <StyledCountryCodeLabel>
          <StyledFlag
            src={availableCalendarProviders[calendar].icon}
            alt={`A calendar icon of ${availableCalendarProviders[calendar].name}`}
          />
          <span>{availableCalendarProviders[calendar].name}</span>
        </StyledCountryCodeLabel>
      ),
      value: availableCalendarProviders[calendar].app,
    })
  );

  // Check calendar connection status on component mount
  useEffect(() => {
    const checkConnection = async () => {
      if (agentId) {
        const connected = await checkCalendarConnection(agentId);
        setIsConnected(connected);
      }
    };
    checkConnection();
  }, [agentId]);

  // Don't render anything if the capability is not selected
  if (!shouldDisplayBookAppointments) {
    return null;
  }

  return (
    <Box
      sx={{
        border: '1px solid lightgrey',
        borderRadius: '4px',
        padding: '1rem',
        backgroundColor: 'white',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: '1rem' }}
      >
        <h3 style={{ margin: 0 }}>{t('Book Appointments')}</h3>
        {fields.length > 0 && (
          <IconButton
            onClick={handleRemoveCapability}
            sx={{
              height: '32px',
              width: '32px',
              color: 'error.main',
              border: '1px solid',
              borderColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.main',
                color: 'white',
              },
            }}
            title={t('Remove Book Appointments capability')}
          >
            <Close />
          </IconButton>
        )}
      </Stack>
      {fields.map((field, index: number) => (
        <Box key={field.id}>
          <StyledFormItem flexDirection="row" gap="1rem">
            <Stack direction="column" width="50%">
              <Text type="label">{`Working Hours Start Time`}</Text>
              <Controller
                name={`insert_appointment.${index}.start_time`}
                control={control}
                rules={{
                  required: 'Start time is required',
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Dropdown
                      {...field}
                      options={workingHoursStartTimeOptions}
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleStartTimeChange(e);
                      }}
                      required
                    />
                    {error && (
                      <Text
                        type="body2"
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {error.message}
                      </Text>
                    )}
                  </div>
                )}
              />
            </Stack>
            <Stack direction="column" width="50%">
              <Text type="label">{`Working Hours End Time`}</Text>
              <Controller
                name={`insert_appointment.${index}.end_time`}
                control={control}
                rules={{
                  required: 'End time is required',
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Dropdown
                      {...field}
                      options={workingHoursEndTimeOptions}
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleEndTimeChange(e);
                      }}
                      required
                    />
                    {error && (
                      <Text
                        type="body2"
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {error.message}
                      </Text>
                    )}
                    {timeError && (
                      <Text
                        type="body2"
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {timeError}
                      </Text>
                    )}
                  </div>
                )}
              />
            </Stack>
          </StyledFormItem>
          <StyledFormItem flexDirection="row" gap="1rem">
            <Stack direction="column" width="50%">
              <Text type="label">{`Minimum Time In Advance For Booking`}</Text>
              <Controller
                name={`insert_appointment.${index}.min_advance_notice_hours`}
                control={control}
                rules={{
                  required: 'Minimum time in advance is required',
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Dropdown
                      {...field}
                      options={minimumTimeInAdvanceOptions}
                      fullWidth
                      required
                    />
                    {error && (
                      <Text
                        type="body2"
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {error.message}
                      </Text>
                    )}
                  </div>
                )}
              />
            </Stack>
            <Stack direction="column" width="50%">
              <Text type="label">{`Appointment Length`}</Text>
              <Controller
                name={`insert_appointment.${index}.appointment_length_minutes`}
                control={control}
                rules={{
                  required: 'Appointment length is required',
                }}
                render={({ field, fieldState: { error } }) => (
                  <div>
                    <Dropdown
                      {...field}
                      options={appointmentLengths}
                      fullWidth
                      required
                    />
                    {error && (
                      <Text
                        type="body2"
                        style={{
                          color: 'red',
                          fontSize: '12px',
                          marginTop: '4px',
                        }}
                      >
                        {error.message}
                      </Text>
                    )}
                  </div>
                )}
              />
            </Stack>
          </StyledFormItem>
          <StyledFormItem flexDirection="row" gap="1rem">
            <Stack direction="column" flexGrow={1} width="50%">
              {isConnected && (
                <>
                  {(() => {
                    const selectedApp = bookAppointmentsValues?.[index]?.app;
                    const calendarInfo =
                      availableCalendarProviders[selectedApp];
                    return (
                      <ConnectedCalendarIndicator
                        iconPath={calendarInfo?.icon}
                        calendarProvider={calendarInfo?.name}
                      />
                    );
                  })()}
                </>
              )}
              {!isConnected && (
                <>
                  <Text type="label">{`Calendars`}</Text>
                  <Controller
                    name={`insert_appointment.${index}.app`}
                    control={control}
                    defaultValue=""
                    rules={{
                      required: 'Calendar is required',
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <div>
                        <Dropdown
                          field={field}
                          options={listOfSupportedCalendars}
                          fullWidth
                          required
                        />
                        {error && (
                          <Text
                            type="body2"
                            style={{
                              color: 'red',
                              fontSize: '12px',
                              marginTop: '4px',
                            }}
                          >
                            {error.message}
                          </Text>
                        )}
                      </div>
                    )}
                  />
                </>
              )}
            </Stack>
            <Stack
              direction="column"
              gap="1rem"
              justifyContent="flex-start"
              paddingBottom="3px"
              width="50%"
              marginTop="28px"
            >
              {!isConnected && (
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    const currentCalendarProvider =
                      bookAppointmentsValues?.[index]?.app;
                    if (currentCalendarProvider) {
                      handleConnectToCalendar(
                        agentId,
                        currentCalendarProvider,
                        workflow
                      );
                    }
                  }}
                  disabled={
                    !bookAppointmentsValues?.[index]?.start_time ||
                    !bookAppointmentsValues?.[index]?.end_time ||
                    !bookAppointmentsValues?.[index]
                      ?.min_advance_notice_hours ||
                    !bookAppointmentsValues?.[index]
                      ?.appointment_length_minutes ||
                    !bookAppointmentsValues?.[index]?.app
                  }
                  size="large"
                  padding="0"
                >
                  {t('Connect Calendar')}
                </Button>
              )}
            </Stack>
          </StyledFormItem>
        </Box>
      ))}
    </Box>
  );
};

export default BookAppointmentsCapabilitySection;
